import { TextField as MuiTextField } from '@mui/material';
import { useId } from 'react';
import { componentSizes } from '../../theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { Box } from './Box';
import { formFieldLabelSx } from './formFieldStyles';
import {
  formFieldPlaceholderTypographySx,
  formFieldTypographySx,
  resolveFormFieldBackgroundColor,
  type FormFieldStyleVariant,
} from './formFieldVariants';
import { FormHelperText } from './FormHelperText';
import { Text } from './Text';
import type { SpacingToken } from '../../theme/tokens';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import type { ReactElement, ReactNode } from 'react';

export type TextFieldProps = Omit<MuiTextFieldProps, 'label' | 'helperText'> & {
  label?: ReactNode;
  helperText?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  inputHeight?: number;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  labelGap?: SpacingToken;
};

export const TextField = ({
  id,
  label,
  helperText,
  helpText,
  errorText,
  fullWidth = true,
  size = 'small',
  inputHeight,
  fieldStyle = 'default',
  inputBackgroundColor,
  labelGap = 'sm',
  sx,
  error,
  required,
  ...rest
}: TextFieldProps): ReactElement => {
  const resolvedInputHeight = inputHeight ?? componentSizes.auth.inputHeight;
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );
  const resolvedHelpText = helpText ?? (error ? undefined : helperText);
  const resolvedErrorText = errorText ?? (error ? helperText : undefined);
  const resolvedHelperText = resolvedErrorText ?? resolvedHelpText;
  const showError = error ? true : Boolean(resolvedErrorText);
  const fallbackId = useId();
  const resolvedId = id ?? fallbackId;
  const helperId = resolvedHelperText ? `${resolvedId}-helper` : undefined;
  const { 'aria-describedby': ariaDescribedBy, ...restProps } = rest;
  const resolvedDescribedBy =
    helperId && ariaDescribedBy ? `${ariaDescribedBy} ${helperId}` : (ariaDescribedBy ?? helperId);
  const baseSx: MuiTextFieldProps['sx'] = (theme) => ({
    '& .MuiInputBase-root': {
      backgroundColor: resolvedInputBackgroundColor,
      minHeight: `${resolvedInputHeight}px !important`,
      height: `${resolvedInputHeight}px !important`,
      maxHeight: `${resolvedInputHeight}px !important`,
      boxSizing: 'border-box',
      ...formFieldTypographySx,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: r('md'),
      minHeight: `${resolvedInputHeight}px !important`,
      height: `${resolvedInputHeight}px !important`,
      backgroundColor: resolvedInputBackgroundColor,
      ...formFieldTypographySx,
    },
    '& .MuiOutlinedInput-input': {
      height: '100%',
      boxSizing: 'border-box',
      paddingTop: s('none'),
      paddingBottom: s('none'),
      paddingLeft: s('lg'),
      paddingRight: s('md'),
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
    },
    '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
      {
        ...formFieldPlaceholderTypographySx,
      },
    '& input': {
      ...formFieldTypographySx,
    },
  });
  const mergedSx = [baseSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])].filter(
    Boolean,
  ) as MuiTextFieldProps['sx'];

  return (
    <Box sx={{ display: 'grid' }}>
      {label ? (
        <Box sx={{ mb: s(labelGap) }}>
          <Text role="body" component="label" htmlFor={resolvedId} sx={formFieldLabelSx}>
            {label}
          </Text>
        </Box>
      ) : null}
      <MuiTextField
        {...restProps}
        id={resolvedId}
        label={undefined}
        error={showError}
        required={required}
        fullWidth={fullWidth}
        size={size}
        variant="outlined"
        helperText={undefined}
        aria-describedby={resolvedDescribedBy}
        sx={mergedSx}
      />
      {resolvedHelperText ? (
        <FormHelperText id={helperId} error={showError} sx={{ mt: s('xs') }}>
          {resolvedHelperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};
